// store/modules/product.js
import api from "@/utils/axios";

const PRODUCT_API_URL = `/products`;

export default {
  namespaced: true,
  state: {
    product: null,
    groups: [],
    groupMeta: {},
    products: [],
    meta: {},
    loading: false,
    error: null,
  },
  mutations: {
    SET_PRODUCT(state, product) {
      state.product = product;
    },
    SET_PRODUCTS(state, products) {
      state.products = products;
    },
    SET_META(state, meta) {
      state.meta = meta;
    },
    SET_GROUPS(state, groups) {
      state.groups = groups;
    },
    SET_GROUP_META(state, meta) {
      state.groupMeta = meta;
    },
    SET_LOADING(state, status) {
      state.loading = status;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    CLEAR_PRODUCT(state) {
      state.product = null;
      state.error = null;
    },
    CLEAR_PRODUCTS(state) {
      state.products = [];
      state.meta = {};
      state.error = null;
    },
    CLEAR_GROUPS(state) {
      state.groups = [];
      state.groupMeta = {};
    },
    UPDATE_PRODUCT_IN_LIST(state, updatedProduct) {
      const index = state.products.findIndex((p) => p.id === updatedProduct.id);
      if (index !== -1) {
        state.products.splice(index, 1, updatedProduct);
      }
    },
  },
  actions: {
    async fetchProduct({ commit }, productproductId) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        const res = await api.get(`${PRODUCT_API_URL}/${productproductId}/`);
        commit("SET_PRODUCT", res.data.data);
        return res.data;
      } catch (err) {
        commit(
          "SET_ERROR",
          err.response?.data?.error || "Failed to fetch product"
        );
        return null;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async selectProducts({ commit }, searchTerm = "") {
      commit("SET_ERROR", null);
      try {
        const res = await api.get(`${PRODUCT_API_URL}/select/`, {
          params: { q: searchTerm },
        });
        return res.data.data;
      } catch (err) {
        commit(
          "SET_ERROR",
          err.response?.data?.error || "Failed to fetch categories"
        );
        return [];
      }
    },

    async filterProducts({ commit }, data = {}) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        const res = await api.post(`${PRODUCT_API_URL}/filter/`, data);
        commit("SET_PRODUCTS", res.data.data);
        commit("SET_META", res.data.meta);
        return res.data;
      } catch (err) {
        commit(
          "SET_ERROR",
          err.response?.data?.error || "Failed to fetch products"
        );
        return null;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async lazyLoadingProducts({ commit, state }, data = {}) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        const res = await api.post(`${PRODUCT_API_URL}/filter/`, data);
        const newProducts = res.data.data;
        const updatedProducts = [...state.products, ...newProducts];
        commit("SET_PRODUCTS", updatedProducts);
        commit("SET_META", res.data.meta);
        return res.data;
      } catch (err) {
        commit(
          "SET_ERROR",
          err.response?.data?.error || "Failed to load more products"
        );
        return null;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async addNewProduct({ commit }, data) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        const res = await api.post(`${PRODUCT_API_URL}/new/`, data);
        commit("SET_PRODUCT", res.data.data);
        return res.data;
      } catch (err) {
        commit(
          "SET_ERROR",
          err.response?.data?.error || "Failed to create product"
        );
        return null;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async generateBarcode({ commit }) {
      commit("SET_ERROR", null);
      try {
        const res = await api.post(`${PRODUCT_API_URL}/barcode/`);
        return res.data.barcode;
      } catch (err) {
        commit(
          "SET_ERROR",
          err.response?.data?.error || "Failed to generate barcode"
        );
        return null;
      }
    },

    async updateProduct({ commit }, { productId, data }) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        const res = await api.post(`${PRODUCT_API_URL}/${productId}/`, data);
        commit("SET_PRODUCT", res.data.data);
        return res.data;
      } catch (err) {
        commit(
          "SET_ERROR",
          err.response?.data?.error || "Failed to update product"
        );
        return null;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async patchProduct({ commit }, { productId, data }) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        const res = await api.patch(`${PRODUCT_API_URL}/${productId}/`, data, {
          successNotify: true,
          errorNotify: true,
        });
        commit("SET_PRODUCT", res.data.data);
        return res.data;
      } catch (err) {
        commit(
          "SET_ERROR",
          err.response?.data?.error || "Failed to update product"
        );
        return null;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async deleteProduct({ commit }, productId) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        await api.delete(`${PRODUCT_API_URL}/${productId}/`);
        commit("CLEAR_PRODUCT_STATE");
        return true;
      } catch (err) {
        commit(
          "SET_ERROR",
          err.response?.data?.error || "Failed to delete product"
        );
        return false;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    clearProduct({ commit }) {
      commit("CLEAR_PRODUCT");
    },

    clearProducts({ commit }) {
      commit("CLEAR_PRODUCTS");
    },
    clearGroups({ commit }) {
      commit("CLEAR_GROUPS");
    },

    async refreshSingleProduct({ commit }, productId) {
      try {
        const res = await api.get(`${PRODUCT_API_URL}/${productId}/`);
        commit("UPDATE_PRODUCT_IN_LIST", res.data.data);
        return res.data.data;
      } catch (err) {
        console.error("Failed to refresh product", productId, err);
        return null;
      }
    },
  },
  getters: {
    product: (state) => state.product,
    products: (state) => state.products,
    productMeta: (state) => state.meta,
    productLoading: (state) => state.loading,
    productError: (state) => state.error,
    groups: (state) => state.groups,
    groupMeta: (state) => state.groupMeta,
  },
};
